import type { ReactNode } from 'react';
import { AddButton, ItemCard } from './fields';

type Mode = 'default' | 'hidden' | 'custom';

function modeOf<T>(value: T[] | undefined, allowHidden: boolean): Mode {
  if (value === undefined) return 'default';
  if (value.length === 0) return allowHidden ? 'hidden' : 'default';
  return 'custom';
}

/** The omit-vs-empty-vs-custom control used by FAQ, testimonials, and
 *  timeline: `undefined` = the built-in defaults, `[]` = hidden, a populated
 *  array = custom entries. Schema semantics, made explicit as three buttons
 *  instead of something Henry has to discover.
 *
 *  Set `allowHidden={false}` for fields where the renderer treats `[]` the
 *  same as `undefined` (timeline): the "Hide section" button doesn't apply
 *  there, and clearing out the last custom entry falls back to `undefined`
 *  instead of leaving a `[]` that would silently render the defaults. */
export default function OmitEmptyCustom<T>({
  label,
  hint,
  defaultsLabel,
  value,
  onChange,
  makeBlank,
  disabled,
  itemLabel = 'entry',
  allowHidden = true,
  children,
}: {
  label: string;
  hint?: ReactNode;
  defaultsLabel: string;
  value: T[] | undefined;
  onChange: (next: T[] | undefined) => void;
  makeBlank: () => T;
  disabled?: boolean;
  itemLabel?: string;
  allowHidden?: boolean;
  children: (item: T, update: (patch: Partial<T>) => void, index: number) => ReactNode;
}) {
  const mode = modeOf(value, allowHidden);

  function setMode(next: Mode) {
    if (next === 'default') onChange(undefined);
    else if (next === 'hidden') onChange([]);
    else if (mode !== 'custom') onChange([makeBlank()]);
  }

  function updateItem(index: number, patch: Partial<T>) {
    if (!value) return;
    onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function removeItem(index: number) {
    if (!value) return;
    const next = value.filter((_, i) => i !== index);
    // Without a "hidden" option, an empty custom list and "use defaults"
    // are the same thing on the live proposal, so drop back to `undefined`
    // rather than leaving `[]` sitting there looking like a distinct state.
    onChange(next.length === 0 && !allowHidden ? undefined : next);
  }

  return (
    <div className="space-y-3">
      <div>
        <span className="text-sm font-medium text-ink">{label}</span>
        {hint && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <ModeButton active={mode === 'default'} onClick={() => setMode('default')} disabled={disabled}>
          Use defaults
        </ModeButton>
        {allowHidden && (
          <ModeButton active={mode === 'hidden'} onClick={() => setMode('hidden')} disabled={disabled}>
            Hide section
          </ModeButton>
        )}
        <ModeButton active={mode === 'custom'} onClick={() => setMode('custom')} disabled={disabled}>
          Customize
        </ModeButton>
      </div>

      {mode === 'default' && <p className="text-xs text-ink-faint">Showing {defaultsLabel}.</p>}
      {mode === 'hidden' && <p className="text-xs text-ink-faint">Section is hidden on the live proposal.</p>}

      {mode === 'custom' && value && (
        <div className="space-y-3">
          {value.map((item, index) => (
            <ItemCard key={index} onRemove={() => removeItem(index)} disabled={disabled}>
              {children(item, (patch) => updateItem(index, patch), index)}
            </ItemCard>
          ))}
          <AddButton onClick={() => onChange([...(value ?? []), makeBlank()])} disabled={disabled}>
            + Add {itemLabel}
          </AddButton>
          {value.length === 0 && allowHidden && (
            <p className="text-xs text-ink-faint">No entries. This is the same as hiding the section.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ' +
        (active ? 'border-accent bg-accent-tint text-accent-deep' : 'border-line text-ink-soft hover:border-ink-faint')
      }
    >
      {children}
    </button>
  );
}
